"use client";

import { useMemo } from "react";
import { useTripData } from "@/lib/trip-data-context";
import { formatCurrency } from "@/lib/format";
import { TabLoading } from "@/components/trip/ui";
import type { BudgetEntry } from "@/lib/types";

const categoryLabels: Record<string, string> = {
  flights: "Flights",
  hotels: "Hotels",
  activities: "Activities & Tickets",
  car: "Rental Car",
  food: "Food & Dining",
  other: "Transport & Other",
};

export default function BudgetPage() {
  const { data, loading } = useTripData();

  const { byCategory, total, totalCost, days, dailyAvg, dailyExcluded } = useMemo(() => {
    const entries = data?.budget ?? [];
    // Inclusive trip length from the trip's own dates (YYYY-MM-DD).
    const { start, end } = data?.trip.dates ?? {};
    const days =
      start && end ? Math.round((Date.parse(end) - Date.parse(start)) / 86_400_000) + 1 : null;
    // Per-day average of on-trip spend: flights never count (they don't scale
    // with days), and entries flagged excludeFromDaily are spend outside the
    // trip days (e.g. a positioning-week bucket).
    const dailyEntries = entries.filter((e) => e.category !== "flights" && !e.excludeFromDaily);
    const dailyTotal = dailyEntries.reduce((sum, e) => sum + e.amount, 0);
    return {
      byCategory: entries.reduce((acc, e) => {
        (acc[e.category] ??= []).push(e);
        return acc;
      }, {} as Record<string, BudgetEntry[]>),
      total: entries.reduce((sum, e) => sum + e.amount, 0),
      totalCost: entries.reduce((sum, e) => sum + (e.totalCost ?? e.amount), 0),
      days,
      dailyAvg: days ? dailyTotal / days : null,
      dailyExcluded: entries.filter((e) => e.excludeFromDaily).length,
    };
  }, [data]);

  if (loading || !data) {
    return <TabLoading />;
  }

  return (
    <div>
      <p style={{ fontSize: 14, color: "var(--text2)", lineHeight: 1.7, margin: "0 0 24px" }}>
        Two people, all-in. <strong style={{ color: "var(--green)" }}>BOOKED</strong> = confirmed. Amounts are net out-of-pocket; <span style={{ textDecoration: "line-through", opacity: 0.7 }}>strike-through</span> = sticker price before reimbursement or travel credit.
      </p>

      {Object.entries(byCategory).map(([cat, items]) => {
        const catTotal = items.reduce((s, e) => s + e.amount, 0);
        const catTotalCost = items.reduce((s, e) => s + (e.totalCost ?? e.amount), 0);
        const catHasDiff = catTotalCost !== catTotal;
        return (
          <div key={cat} style={{ marginBottom: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", borderBottom: "2px solid var(--text)", paddingBottom: 6, marginBottom: 10 }}>
              <span style={{ fontWeight: 700, fontSize: 14, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                {categoryLabels[cat] || cat}
              </span>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontFamily: "var(--mono)", fontWeight: 600, fontSize: 14 }}>
                  {formatCurrency(catTotal)}
                </div>
                {catHasDiff && (
                  <div style={{ fontFamily: "var(--mono)", fontSize: 11, color: "var(--text3)", marginTop: 2, textDecoration: "line-through", opacity: 0.7 }}>
                    {formatCurrency(catTotalCost)}
                  </div>
                )}
              </div>
            </div>
            {items.map((entry) => {
              const hasTotal = entry.totalCost != null && entry.totalCost !== entry.amount;
              return (
                <div key={entry.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "5px 0", fontSize: 13 }}>
                  <div style={{ flex: 1, color: "var(--text2)" }}>
                    {entry.description}
                    {entry.paid && (
                      <div style={{ fontSize: 11, marginTop: 2, color: "var(--green)", fontWeight: 600 }}>
                        BOOKED
                      </div>
                    )}
                    {entry.excludeFromDaily && (
                      <div style={{ fontSize: 11, marginTop: 2, color: "var(--text3)", fontStyle: "italic" }}>
                        not in daily average
                      </div>
                    )}
                    {entry.costNote && (
                      <div style={{ fontSize: 11, marginTop: 2, color: "var(--text3)", fontStyle: "italic" }}>
                        {entry.costNote}
                      </div>
                    )}
                  </div>
                  <div style={{ marginLeft: 16, textAlign: "right", whiteSpace: "nowrap" }}>
                    <div style={{ fontFamily: "var(--mono)", fontSize: 13, color: "var(--text3)" }}>
                      {formatCurrency(entry.amount)}
                    </div>
                    {hasTotal && (
                      <div style={{ fontFamily: "var(--mono)", fontSize: 11, color: "var(--text3)", marginTop: 2, textDecoration: "line-through", opacity: 0.7 }}>
                        {formatCurrency(entry.totalCost!)}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", borderTop: "3px solid var(--text)", paddingTop: 14, marginTop: 8 }}>
        <span style={{ fontWeight: 700, fontSize: 16 }}>ESTIMATED TOTAL (2 people)</span>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontFamily: "var(--mono)", fontWeight: 700, fontSize: 18, color: "var(--accent)" }}>
            {formatCurrency(total)}
          </div>
          {totalCost !== total && (
            <div style={{ fontFamily: "var(--mono)", fontSize: 12, color: "var(--text3)", marginTop: 2, textDecoration: "line-through", opacity: 0.7 }}>
              {formatCurrency(totalCost)}
            </div>
          )}
        </div>
      </div>
      <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 6, textAlign: "right" }}>
        Per person: {formatCurrency(Math.round(total / 2))}
        {totalCost !== total && (
          <span style={{ textDecoration: "line-through", opacity: 0.7, marginLeft: 8 }}>
            {formatCurrency(Math.round(totalCost / 2))}
          </span>
        )}
      </div>
      {days != null && dailyAvg != null && (
        <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 4, textAlign: "right" }}>
          {days} days · non-airfare average {formatCurrency(Math.round(dailyAvg))}/day
          <span style={{ opacity: 0.8 }}>
            {" "}(excludes flights{dailyExcluded > 0 ? ` + ${dailyExcluded} flagged entr${dailyExcluded === 1 ? "y" : "ies"}` : ""})
          </span>
        </div>
      )}
    </div>
  );
}
